import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faHourglassHalf, faLayerGroup, faLocationDot, faMoneyBill } from "@fortawesome/free-solid-svg-icons";
import { renderSettingsResourceLabel } from "../../../../utils/helpers/settings";
import { renderDurationAsFormat } from "../../../../utils/helpers/date";
import { useGroups } from "../../../Settings/General/OrganisationLevelGroups/context";
import { useOrgSettingsResourceLabel } from "../../../../utils/hooks/useOrgSettingsResourceLabel";
import { smallText } from "./constants";

const baseFields = [
    { key: "location", label: "location", icon: faLocationDot },
    { key: "job", label: "job", icon: faBriefcase },
    { key: "payCode", label: "pay-code", icon: faMoneyBill },
];

// Distinct labels of a field across the shift's sections, falling back to the shift itself.
const getLabels = (shift, key, renderLabel) => {
    const sources = shift.sections?.length ? shift.sections : [shift];
    const labels = sources
        .map((source) => source?.[key])
        .filter(Boolean)
        .map((value) => typeof value === "string" ? value : renderLabel(value));
    return [...new Set(labels.filter(Boolean))];
};

const ShiftDetails = ({ shift }) => {
    const { t } = useTranslation();
    const { groups } = useGroups();
    const renderLevelLabel = useOrgSettingsResourceLabel();

    const levelFields = useMemo(() => (groups ?? [])
        .filter((group) => group?.level)
        .sort((a, b) => a.level.localeCompare(b.level))
        .map((group) => ({
            key: group.level.replace("_", ""),
            label: group.description,
            icon: faLayerGroup,
            isLevel: true,
        })), [groups]);

    const details = [...baseFields, ...levelFields]
        .map((field) => ({
            ...field,
            values: getLabels(shift, field.key, field.isLevel ? renderLevelLabel : renderSettingsResourceLabel),
        }))
        .filter((field) => field.values.length);

    if (!details.length && !shift.duration) return null;

    return (
        <div className="d-flex flex-column text-muted" style={{ ...smallText, gap: 2 }}>
            {!!shift.duration && (
                <span>
                    <FontAwesomeIcon icon={faHourglassHalf} className="mr-2" fixedWidth />
                    {t("duration")}: <span className="text-dark">{renderDurationAsFormat(shift.duration, "HH:mm")} {t("hours")}</span>
                </span>
            )}
            {details.map(({ key, label, icon, values }) => (
                <span key={key}>
                    <FontAwesomeIcon icon={icon} className="mr-2" fixedWidth />
                    {t(label)}: <span className="text-dark">{values.join(", ")}</span>
                </span>
            ))}
        </div>
    );
};

export default ShiftDetails;
